import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getExercise } from '../lib/api';
import { toast } from 'sonner';
import { 
  Trophy, Target, Keyboard, BookOpen, 
  ArrowLeft, Home, ChevronRight, CheckCircle,
  TrendingUp, Star, Sparkles, Lightbulb
} from 'lucide-react';

const ResultsPage = () => {
  const navigate = useNavigate();
  const { exerciseId, sessionId } = useParams();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExercise = async () => {
      try {
        // Small delay to ensure quiz save completes
        await new Promise(resolve => setTimeout(resolve, 500));
        const res = await getExercise(exerciseId, sessionId);
        setExercise(res.data);
      } catch (error) {
        console.error('Failed to load results:', error);
        // Retry once after a delay
        try {
          await new Promise(resolve => setTimeout(resolve, 1500));
          const res = await getExercise(exerciseId, sessionId);
          setExercise(res.data);
        } catch (retryError) {
          console.error('Retry also failed:', retryError);
          toast.error('Failed to load results. Please go back and try again.');
          // Don't navigate away immediately - let user see the error
        }
      } finally {
        setLoading(false);
      }
    };
    fetchExercise();
  }, [exerciseId, sessionId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Handle case where exercise loaded but no session data
  if (!exercise) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="card-glass p-8 text-center max-w-md">
          <h2 className="font-heading text-xl font-bold mb-4">Unable to Load Results</h2>
          <p className="text-text-secondary mb-6">
            We couldn&apos;t load your results. This might be a temporary issue.
          </p>
          <Button 
            className="btn-primary"
            onClick={() => navigate('/student')}
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const session = exercise?.user_session;
  const previousScores = exercise?.previous_scores;
  
  // Handle missing session gracefully - show what we can
  if (!session) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/student')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Exercise Complete!</h1>
              <p className="text-text-secondary text-sm">{exercise?.title}</p>
            </div>
          </div>
        </header>
        <main className="max-w-5xl mx-auto px-6 py-8">
          <Card className="card-glass p-8 text-center">
            <Trophy className="w-16 h-16 text-neon-yellow mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold mb-4">Great Job!</h2>
            <p className="text-text-secondary mb-6">
              Your exercise has been completed. Your results are being processed.
            </p>
            <Button 
              className="btn-primary"
              onClick={() => navigate('/student')}
            >
              <Home className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Card>
        </main>
      </div>
    );
  }
  
  // New metrics - use from session if available, otherwise calculate
  const typedWords = session?.typed_words || session?.typed_text?.split(/\s+/).filter(w => w.length > 0).length || 0;
  const totalWords = session?.total_words || exercise?.passage?.split(/\s+/).length || 1;
  const correctWords = session?.correct_words || 0;
  
  // Calculate completion - percentage of passage attempted
  const completion = session?.completion ?? Math.min(100, Math.round((typedWords / totalWords) * 100));
  
  // Get accuracy from session, default to 0
  const accuracy = session?.accuracy || 0;
  
  // Get WPM values
  const wpm = session?.actual_wpm || 0;
  const targetWpm = exercise?.target_wpm || 30;
  
  // Get quiz score
  const quizScore = session?.quiz_score || 0;

  // Calculate improvements and bonuses
  const accuracyImprovement = previousScores ? accuracy - previousScores.accuracy : 0;
  const wpmImprovement = previousScores ? wpm - previousScores.wpm : 0;
  
  // Bonus points calculation:
  // +5 points for any improvement in accuracy
  // +3 points for every 5% improvement in accuracy
  // +2 points for any improvement in WPM
  // +1 point for every 5 WPM improvement
  let typingBonus = 0;
  let bonusReasons = [];
  
  if (previousScores) {
    if (accuracyImprovement > 0) {
      typingBonus += 5;
      bonusReasons.push(`+5 for accuracy improvement`);
      
      const extraAccuracyBonus = Math.floor(accuracyImprovement / 5) * 3;
      if (extraAccuracyBonus > 0) {
        typingBonus += extraAccuracyBonus;
        bonusReasons.push(`+${extraAccuracyBonus} for ${Math.floor(accuracyImprovement)}% accuracy gain`);
      }
    }
    
    if (wpmImprovement > 0) {
      typingBonus += 2;
      bonusReasons.push(`+2 for speed improvement`);
      
      const extraWpmBonus = Math.floor(wpmImprovement / 5);
      if (extraWpmBonus > 0) {
        typingBonus += extraWpmBonus;
        bonusReasons.push(`+${extraWpmBonus} for +${Math.floor(wpmImprovement)} WPM gain`);
      }
    }
  }

  // Typing Grade Calculation
  // Base score is primarily from accuracy (what they typed correctly)
  // Completion determines how much of the passage they attempted
  // WPM is treated as a bonus/modifier, not a penalty
  
  // Ensure all values are valid numbers
  const safeCompletion = !isNaN(completion) && completion !== null ? completion : (typedWords > 0 ? 100 : 0);
  const safeAccuracy = !isNaN(accuracy) ? accuracy : 0;
  const safeWpm = !isNaN(wpm) ? wpm : 0;
  
  // WPM performance as percentage of target (capped at 150% for bonus)
  const wpmPerformance = Math.min(150, (safeWpm / targetWpm) * 100);
  
  // Base typing score: primarily accuracy, weighted by completion
  // If they completed 50% of passage with 100% accuracy, they get 50% base score
  // If they completed 100% of passage with 70% accuracy, they get 70% base score
  const baseTypingScore = safeAccuracy * (safeCompletion / 100);
  
  // WPM bonus: up to +15 points if they hit or exceed target WPM
  // This means WPM helps but doesn't hurt - slow but accurate is still rewarded
  const wpmBonus = Math.min(15, (wpmPerformance / 100) * 15);
  
  // Final score with bonuses
  const typingScoreWithBonus = Math.min(100, Math.max(0, baseTypingScore + wpmBonus + typingBonus));
  
  // Quiz Grade: straight percentage
  const quizGrade = quizScore || 0;

  // Final Score: (accuracy + completion + quiz_score) / 3
  // Use from session if available, otherwise calculate
  const finalScore = session?.final_score ?? Math.round((safeAccuracy + safeCompletion + quizGrade) / 3);

  const getLetterGrade = (score) => {
    if (score >= 90) return { grade: 'A', color: 'text-neon-green' };
    if (score >= 80) return { grade: 'B', color: 'text-neon-lime' };
    if (score >= 70) return { grade: 'C', color: 'text-neon-yellow' };
    if (score >= 60) return { grade: 'D', color: 'text-neon-orange' };
    return { grade: 'F', color: 'text-neon-red' };
  };

  const typingGrade = getLetterGrade(typingScoreWithBonus);
  const comprehensionGrade = getLetterGrade(quizGrade);
  const finalGrade = getLetterGrade(finalScore);
  
  const hasImprovement = typingBonus > 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/student')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Exercise Complete!</h1>
            <p className="text-text-secondary text-sm">{exercise?.title}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Improvement Banner */}
        {hasImprovement && (
          <div className="mb-6 p-4 bg-gradient-to-r from-status-success/20 to-primary/20 border border-status-success/30 rounded-xl flex items-center gap-4 animate-fade-in">
            <div className="w-12 h-12 rounded-full bg-status-success/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-status-success" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-status-success flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Great Improvement!
              </h3>
              <p className="text-sm text-text-secondary">
                You earned <span className="text-status-success font-bold">+{typingBonus} bonus points</span> for improving over your last exercise!
              </p>
            </div>
          </div>
        )}

        {/* Three Grade Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Typing Grade Card */}
          <Card className="card-glass p-6 text-center neon-border relative overflow-hidden" data-testid="typing-grade-card">
            {hasImprovement && (
              <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-status-success/20 rounded-full">
                <Star className="w-3 h-3 text-status-success" />
                <span className="text-xs text-status-success font-bold">+{typingBonus}</span>
              </div>
            )}
            <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
              <Keyboard className="w-7 h-7 text-primary" />
            </div>
            <h2 className="font-heading text-lg font-bold mb-1">Typing</h2>
            <p className="text-text-muted text-xs mb-3">Accuracy × Completion</p>
            <div className={`text-5xl font-heading font-bold ${typingGrade.color}`} data-testid="typing-grade">
              {typingGrade.grade}
            </div>
            <p className="text-text-secondary text-sm mt-2">
              {Math.round(baseTypingScore)}%
            </p>
          </Card>

          {/* Quiz Grade Card */}
          <Card className="card-glass p-6 text-center neon-border-secondary" data-testid="quiz-grade-card">
            <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-3">
              <BookOpen className="w-7 h-7 text-secondary" />
            </div>
            <h2 className="font-heading text-lg font-bold mb-1">Quiz</h2>
            <p className="text-text-muted text-xs mb-3">Comprehension Score</p>
            <div className={`text-5xl font-heading font-bold ${comprehensionGrade.color}`} data-testid="quiz-grade">
              {comprehensionGrade.grade}
            </div>
            <p className="text-text-secondary text-sm mt-2">{Math.round(quizGrade)}%</p>
          </Card>

          {/* Final Grade Card - NEW */}
          <Card className="card-glass p-6 text-center relative overflow-hidden border-2 border-neon-yellow/50" data-testid="final-grade-card">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-neon-yellow to-secondary" />
            <div className="w-14 h-14 rounded-full bg-neon-yellow/20 flex items-center justify-center mx-auto mb-3">
              <Trophy className="w-7 h-7 text-neon-yellow" />
            </div>
            <h2 className="font-heading text-lg font-bold mb-1">Final Grade</h2>
            <p className="text-text-muted text-xs mb-3">For Gradebook</p>
            <div className={`text-5xl font-heading font-bold ${finalGrade.color}`} data-testid="final-grade">
              {finalGrade.grade}
            </div>
            <p className="text-text-secondary text-sm mt-2">{Math.round(finalScore)}%</p>
            <p className="text-text-muted text-xs mt-1">(Acc + Comp + Quiz) ÷ 3</p>
          </Card>
        </div>

        {/* Bonus Breakdown (if any) */}
        {hasImprovement && bonusReasons.length > 0 && (
          <Card className="card-glass p-4 mb-8" data-testid="bonus-breakdown">
            <h3 className="font-heading text-sm font-bold mb-3 flex items-center gap-2">
              <Star className="w-4 h-4 text-status-success" />
              Bonus Points Breakdown
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {bonusReasons.map((reason, idx) => (
                <div key={idx} className="text-sm px-3 py-2 bg-status-success/10 rounded-lg text-status-success">
                  {reason}
                </div>
              ))}
            </div>
            {previousScores && (
              <p className="text-xs text-text-muted mt-3">
                Compared to last exercise: {previousScores.accuracy}% accuracy, {previousScores.wpm} WPM
              </p>
            )}
          </Card>
        )}

        {/* Detailed Stats */}
        <h3 className="font-heading text-lg font-bold mb-4">Detailed Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {/* Typing Accuracy */}
          <Card className="card-glass p-4" data-testid="accuracy-stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <p className="text-text-secondary text-sm">Accuracy</p>
            </div>
            <p className={`font-heading text-2xl font-bold ${accuracy >= 80 ? 'text-neon-green' : accuracy >= 60 ? 'text-neon-yellow' : 'text-neon-red'}`}>
              {accuracy}%
            </p>
            {previousScores && (
              <p className={`text-xs mt-1 ${accuracyImprovement > 0 ? 'text-neon-green' : accuracyImprovement < 0 ? 'text-neon-red' : 'text-text-muted'}`}>
                {accuracyImprovement > 0 ? '↑' : accuracyImprovement < 0 ? '↓' : '→'} {Math.abs(accuracyImprovement).toFixed(1)}% vs last
              </p>
            )}
            <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${accuracy}%` }} />
            </div>
          </Card>

          {/* Completion */}
          <Card className="card-glass p-4" data-testid="completion-stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-neon-yellow/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-neon-yellow" />
              </div>
              <p className="text-text-secondary text-sm">Completion</p>
            </div>
            <p className={`font-heading text-2xl font-bold ${completion >= 80 ? 'text-neon-green' : completion >= 50 ? 'text-neon-yellow' : 'text-neon-red'}`}>
              {completion}%
            </p>
            <p className="text-text-muted text-xs mt-1">{typedWords}/{totalWords} words</p>
            <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-neon-yellow" style={{ width: `${completion}%` }} />
            </div>
          </Card>

          {/* WPM */}
          <Card className="card-glass p-4" data-testid="wpm-stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                <Keyboard className="w-5 h-5 text-secondary" />
              </div>
              <p className="text-text-secondary text-sm">Speed</p>
            </div>
            <p className={`font-heading text-2xl font-bold ${wpm >= targetWpm ? 'text-status-success' : 'text-secondary'}`}>
              {wpm} <span className="text-sm font-normal text-text-muted">WPM</span>
            </p>
            {previousScores && (
              <p className={`text-xs mt-1 ${wpmImprovement > 0 ? 'text-status-success' : wpmImprovement < 0 ? 'text-status-error' : 'text-text-muted'}`}>
                {wpmImprovement > 0 ? '↑' : wpmImprovement < 0 ? '↓' : '→'} {Math.abs(wpmImprovement).toFixed(1)} vs last
              </p>
            )}
            <p className="text-text-muted text-xs mt-1">Target: {targetWpm}</p>
          </Card>

          {/* Quiz Score */}
          <Card className="card-glass p-4" data-testid="quiz-stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-status-success/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-status-success" />
              </div>
              <p className="text-text-secondary text-sm">Quiz</p>
            </div>
            <p className={`font-heading text-2xl font-bold ${quizScore >= 80 ? 'text-status-success' : quizScore >= 60 ? 'text-status-warning' : 'text-status-error'}`}>
              {quizScore}%
            </p>
            <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-status-success" style={{ width: `${quizScore}%` }} />
            </div>
          </Card>

          {/* Final Score */}
          <Card className="card-glass p-4 border border-neon-yellow/30" data-testid="final-score-stat">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-neon-yellow/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-neon-yellow" />
              </div>
              <p className="text-text-secondary text-sm">Final</p>
            </div>
            <p className={`font-heading text-2xl font-bold ${finalScore >= 80 ? 'text-neon-green' : finalScore >= 60 ? 'text-neon-yellow' : 'text-neon-red'}`}>
              {finalScore}%
            </p>
            <p className="text-text-muted text-xs mt-1">Gradebook Score</p>
            <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-neon-yellow" style={{ width: `${finalScore}%` }} />
            </div>
          </Card>
        </div>

        {/* Objective Info */}
        <Card className="card-glass p-6 mb-8" data-testid="objective-card">
          <h3 className="font-heading text-lg font-bold mb-2">
            {exercise?.objectives?.length > 1 ? 'ELA Objectives Practiced' : 'ELA Objective Practiced'}
          </h3>
          {exercise?.objectives?.length > 1 ? (
            <div className="space-y-2">
              {exercise.objectives.map((obj, i) => (
                <div key={i} className="p-2 bg-background-surface rounded">
                  <p className="text-primary font-mono text-sm">{obj.code}</p>
                  <p className="text-text-secondary text-sm">{obj.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="text-primary font-mono mb-1">{exercise?.objective_code}</p>
              <p className="text-text-secondary">{exercise?.objective_description}</p>
            </>
          )}
        </Card>

        {/* Quiz Review - Show questions with hints for wrong answers */}
        {session?.quiz_answers && exercise?.quiz_questions && (
          <Card className="card-glass p-6 mb-8" data-testid="quiz-review-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold">Quiz Review</h3>
              <p className="text-sm text-text-muted">
                Hints shown for missed questions
              </p>
            </div>
            <div className="space-y-4">
              {exercise.quiz_questions.map((question, idx) => {
                const studentAnswer = session.quiz_answers[idx]?.answer;
                const isCorrect = studentAnswer === question.correct_answer;
                
                return (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg border ${
                      isCorrect 
                        ? 'bg-neon-green/5 border-neon-green/30' 
                        : 'bg-neon-red/5 border-neon-red/30'
                    }`}
                    data-testid={`quiz-review-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isCorrect ? 'bg-neon-green/20' : 'bg-neon-red/20'
                      }`}>
                        {isCorrect ? (
                          <CheckCircle className="w-4 h-4 text-neon-green" />
                        ) : (
                          <span className="text-neon-red text-xs font-bold">✗</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="font-medium text-sm">
                            <span className="text-text-muted">Q{idx + 1}:</span> {question.question}
                          </p>
                          {/* Show objective code badge */}
                          {question.objective_code && (
                            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-mono rounded flex-shrink-0">
                              {question.objective_code}
                            </span>
                          )}
                        </div>
                        
                        {!isCorrect && (
                          <div className="space-y-2 mb-3">
                            <p className="text-sm">
                              <span className="text-neon-red">Your answer:</span>{' '}
                              <span className="text-text-secondary">
                                {studentAnswer 
                                  ? question.options?.find(o => o.startsWith(studentAnswer)) || studentAnswer
                                  : 'No answer'}
                              </span>
                            </p>
                          </div>
                        )}
                        
                        {/* Show hint for wrong answers - teaches HOW to find the answer */}
                        {!isCorrect && (question.hint || question.explanation) && (
                          <div className="rounded p-3 mt-2 bg-secondary/10 border border-secondary/30">
                            <p className="text-xs text-secondary mb-1 font-medium flex items-center gap-1">
                              <Lightbulb className="w-3 h-3" />
                              How to find this type of answer:
                            </p>
                            <p className="text-sm text-text-secondary">
                              {(question.hint || question.explanation)
                                .replace(/^The correct answer is [A-D]\.?\s*/i, '')
                                .replace(/^The answer is [A-D]\.?\s*/i, '')
                                .replace(/^\s*[A-D] is correct\.?\s*/i, '')
                              }
                            </p>
                            <p className="text-xs text-text-muted mt-2 italic">
                              Try re-reading the passage with this hint in mind!
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            className="btn-secondary rounded-sm px-8"
            onClick={() => navigate('/student')}
            data-testid="back-to-dashboard-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button 
            className="btn-primary rounded-sm px-8"
            onClick={() => navigate('/progress')}
            data-testid="view-progress-btn"
          >
            View My Progress
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ResultsPage;
